import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { ModalMensageria } from './ModalMensageria';
import { Skeleton } from '@mui/material';
// Remove unused import

type Mensageria = {
  id?: string;
  sistemaId?: string;
  nomeTopico: string;
  acessoContainers?: Array<{
    id?: string;
    mensageriaId?: string;
    tipo: number;
    direcao: number;
  }>;
};

type Row = {
  id: string;
  nomeTopico: string;
  isEditing?: boolean;
  isNew?: boolean;
};

type Props = {
  initialRows?: Mensageria[];
  onMensageriasChange?: (mensagerias: Mensageria[]) => void;
  currentSystemId?: string;
  isLoading?: boolean;
};

const convertToRows = (mensagerias: Mensageria[]): Row[] => {
  return (mensagerias || []).map((item, index) => ({
    id: item.id || `temp-${Date.now()}-${index}`,
    nomeTopico: item.nomeTopico,
  }));
};

const convertToMensagerias = (
  rows: Row[],
  mensageriasData: Map<string, Mensageria>
): Mensageria[] => {
  return (rows || [])
    .filter((row) => !row.isNew && !row.isEditing && row.nomeTopico?.trim())
    .map((row) => {
      const existingMensageria = mensageriasData.get(row.id);
      return {
        id: row.id.startsWith('temp-') ? undefined : row.id,
        sistemaId: existingMensageria?.sistemaId,
        nomeTopico: row.nomeTopico.trim(),
        acessoContainers: existingMensageria?.acessoContainers || [],
      };
    });
};

export const ListaMensageria: React.FC<Props> = ({
  initialRows = [],
  onMensageriasChange,
  currentSystemId,
  isLoading = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [mensageriasData, setMensageriasData] = useState<
    Map<string, Mensageria>
  >(new Map());
  const [openModal, setOpenModal] = useState(false);
  const [editingMensageriaId, setEditingMensageriaId] = useState<string | null>(
    null
  );
  const [editingValues, setEditingValues] = useState<{
    [key: string]: {
      nomeTopico: string;
    };
  }>({});

  useEffect(() => {
    if (initialRows && initialRows.length > 0) {
      setRows(convertToRows(initialRows));

      // Criar Map com os dados das mensagerias
      const dataMap = new Map<string, Mensageria>();
      initialRows.forEach((mensageria) => {
        const id = mensageria.id || `temp-${Date.now()}-${Math.random()}`;
        dataMap.set(id, mensageria);
      });
      setMensageriasData(dataMap);
    } else {
      setRows([]);
      setMensageriasData(new Map());
    }
  }, [initialRows]);

  const handleEdit = (id: string) => {
    const mensageria = mensageriasData.get(id);
    if (!mensageria) return;

    setEditingMensageriaId(id);
    setOpenModal(true);
  };

  const handleCancelEdit = (id: string) => {
    setRows(
      rows
        ?.map((r) => {
          if (r.id === id) {
            if (r.isNew) {
              return null;
            }
            return { ...r, isEditing: false };
          }
          return r;
        })
        .filter(Boolean) as Row[]
    );

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);
  };

  const handleSaveEdit = (id: string) => {
    const editingValue = editingValues[id];
    if (!editingValue || !editingValue.nomeTopico?.trim()) {
      return;
    }

    const newRows = rows?.map((r) =>
      r.id === id
        ? {
          ...r,
          id: r.isNew
            ? `mensageria-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`
            : r.id,
          nomeTopico: editingValue.nomeTopico.trim(),
          isEditing: false,
          isNew: false,
        }
        : r
    );

    setRows(newRows);

    // Atualizar o Map de mensagerias com o novo nome
    const updatedMensageriasData = new Map(mensageriasData);
    const mensageriaData = updatedMensageriasData.get(id);
    if (mensageriaData) {
      updatedMensageriasData.set(id, {
        ...mensageriaData,
        nomeTopico: editingValue.nomeTopico.trim(),
      });
      setMensageriasData(updatedMensageriasData);
    }

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    if (onMensageriasChange) {
      const mensageriasResult = convertToMensagerias(
        newRows,
        updatedMensageriasData
      );
      onMensageriasChange(mensageriasResult);
    }
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    if (onMensageriasChange) {
      const mensageriasResult = convertToMensagerias(newRows, mensageriasData);
      onMensageriasChange(mensageriasResult);
    }
  };

  const handleAddNew = () => {
    setEditingMensageriaId(null); // Para indicar que é novo
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingMensageriaId(null);
  };

  const handleSaveModal = (mensageria: any) => {
    if (editingMensageriaId) {
      // Editando mensageria existente
      const updatedRows = rows.map((row) =>
        row.id === editingMensageriaId
          ? { ...row, nomeTopico: mensageria.nomeTopico }
          : row
      );
      setRows(updatedRows);

      const updatedMensageriasData = new Map(mensageriasData);
      const existingMensageria =
        updatedMensageriasData.get(editingMensageriaId);
      if (existingMensageria) {
        updatedMensageriasData.set(editingMensageriaId, {
          ...existingMensageria,
          nomeTopico: mensageria.nomeTopico,
          acessoContainers: mensageria.acessoContainers || [],
        });
        setMensageriasData(updatedMensageriasData);
      }

      if (onMensageriasChange) {
        const mensageriasResult = convertToMensagerias(
          updatedRows,
          updatedMensageriasData
        );
        onMensageriasChange(mensageriasResult);
      }
    } else {
      // Criando nova mensageria
      const newMensageriaId = `mensageria-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const newRow: Row = {
        id: newMensageriaId,
        nomeTopico: mensageria.nomeTopico,
      };

      const newMensageriaData: Mensageria = {
        id: newMensageriaId,
        sistemaId: currentSystemId,
        nomeTopico: mensageria.nomeTopico,
        acessoContainers: mensageria.acessoContainers || [],
      };

      const newRows = [...rows, newRow];
      const newMensageriasData = new Map(mensageriasData);
      newMensageriasData.set(newMensageriaId, newMensageriaData);

      setRows(newRows);
      setMensageriasData(newMensageriasData);

      if (onMensageriasChange) {
        const mensageriasResult = convertToMensagerias(
          newRows,
          newMensageriasData
        );

        onMensageriasChange(mensageriasResult);
      }
    }

    setOpenModal(false);
    setEditingMensageriaId(null);
  };

  const handleEditingValueChange = (id: string, field: string, value: any) => {
    setEditingValues({
      ...editingValues,
      [id]: {
        ...editingValues[id],
        [field]: value,
      },
    });
  };

  const canSave = (id: string) => {
    const editingValue = editingValues[id];
    return editingValue && editingValue.nomeTopico?.trim();
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={80} height={32} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ width: '80px' }}
          onClick={handleAddNew}
          startIcon={<AddIcon />}
        >
          Novo
        </Button>
      )}

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Tópico</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3].map((n) => (
                  <TableRow key={n}>
                    <TableCell>
                      <Skeleton variant="rectangular" width={80} height={30} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={200} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Nenhuma mensageria cadastrada
                </TableCell>
              </TableRow>
            ) : (
              rows?.map((row) => {
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.isEditing ? (
                        <>
                          <Tooltip title="Salvar">
                            <IconButton
                              size="small"
                              onClick={() => handleSaveEdit(row.id)}
                              color="primary"
                              disabled={!canSave(row.id)}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton
                              size="small"
                              onClick={() => handleCancelEdit(row.id)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row.id)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(row.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editingValues[row.id]?.nomeTopico || ''}
                          onChange={(e) =>
                            handleEditingValueChange(
                              row.id,
                              'nomeTopico',
                              e.target.value
                            )
                          }
                          variant="outlined"
                          placeholder="Ex: pedidos.created, usuarios.updated"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (canSave(row.id)) {
                                handleSaveEdit(row.id);
                              }
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              handleCancelEdit(row.id);
                            }
                          }}
                        />
                      ) : (
                        row.nomeTopico
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ModalMensageria
        open={openModal}
        onClose={handleCloseModal}
        onSave={handleSaveModal}
        initialValues={
          editingMensageriaId
            ? (() => {
              const mensageria = mensageriasData.get(editingMensageriaId);
              return {
                id: mensageria?.id,
                nomeTopico: mensageria?.nomeTopico || '',
                acessoContainers: mensageria?.acessoContainers || [],
              };
            })()
            : undefined
        }
      />
    </Box>
  );
};
